// Squelette commun des fenêtres (socle UX, section 9) :
//   <div id="…Overlay" class="dialog-overlay" hidden role="dialog" aria-modal="true" aria-labelledby="…Title">
//     <div class="dialog-card" tabindex="-1">
//       <button class="dialog-close" data-close aria-label="Fermer">×</button> <h2 id="…Title">…</h2> …
// Ouverture : overlay visible, écouteur Échap, focus sur la carte, aria-expanded du déclencheur à true.
// Fermeture : × ou [data-close], clic sur le fond, Échap ; le focus revient au déclencheur. Fermer deux fois est sans
// effet ; une seule instance : un second open() renvoie la même promesse, résolue à la fermeture.

/**
 * @param {HTMLElement} overlay
 * @param {object} [options]
 * @param {HTMLElement|null} [options.trigger]  bouton qui ouvre la fenêtre (aria-expanded, retour du focus)
 * @param {(opts: object) => void} [options.onOpen]   appelé à chaque ouverture, avant l'affichage
 * @param {(result: any) => void} [options.onClose]   appelé à la fermeture, avec la valeur de résolution
 * @param {string} [options.focus]  sélecteur de l'élément à focaliser plutôt que la carte
 * @param {number} [options.backdropGraceMs]  un clic sur le fond dans ce délai après l'ouverture est ignoré (le second appui d'un
 *   double-tap sur le bouton qui ouvre la fenêtre ne doit pas la refermer aussitôt)
 */
export function createDialog(overlay, { trigger = null, onOpen, onClose, focus, backdropGraceMs = 0 } = {}) {
  const card = overlay.querySelector('.dialog-card');
  let pending = null;
  let resolve = null;
  let dismissible = true;
  let returnFocus = null;
  let openedAt = -Infinity;

  const onKey = (event) => {
    if (event.key === 'Escape' && dismissible) {
      event.stopPropagation();
      close(null);
    }
  };

  /** Fenêtre obligatoire (dismissible: false) : ni croix, ni fond, ni Échap. */
  function setDismissible(value) {
    dismissible = value !== false;
    overlay.querySelectorAll('[data-close]').forEach((el) => { el.hidden = !dismissible; });
  }

  function open(options = {}) {
    if (pending) return pending;
    pending = new Promise((done) => { resolve = done; });
    setDismissible(options.dismissible);
    returnFocus = trigger ?? document.activeElement;
    onOpen?.(options);
    overlay.hidden = false;
    openedAt = performance.now();
    document.addEventListener('keydown', onKey);
    trigger?.setAttribute('aria-expanded', 'true');
    const target = (focus && overlay.querySelector(focus)) || card;
    target?.focus?.({ preventScroll: true });
    return pending;
  }

  function close(result = null) {
    if (overlay.hidden) return;
    overlay.hidden = true;
    document.removeEventListener('keydown', onKey);
    trigger?.setAttribute('aria-expanded', 'false');
    returnFocus?.focus?.({ preventScroll: true });
    const done = resolve;
    pending = null;
    resolve = null;
    onClose?.(result);
    done?.(result);
  }

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay && dismissible) {
      if (performance.now() - openedAt >= backdropGraceMs) close(null);
    }
    else if (event.target.closest?.('[data-close]') && dismissible) close(null);
  });

  return { open, close, setDismissible, isOpen: () => !overlay.hidden, overlay, card };
}
